import { ElementRef, Directive, HostListener, inject, OnInit, OnDestroy } from "@angular/core";
import { GranularityProperty } from "../../../models/types/balanceGroupingTypes";
import { DeviceDetectorService } from "ngx-device-detector";
import * as d3 from "d3";

import { Scales } from "@bitwarden/web-vault/app/components/charts/shared/scales";
import { Axis } from "@bitwarden/web-vault/app/components/charts/shared/axis";
import { DataLine } from "@bitwarden/web-vault/app/components/charts/shared/data-line";
import { DataPoints } from "@bitwarden/web-vault/app/components/charts/shared/data-points";
import { GroupingSelection } from "@bitwarden/web-vault/app/services/store/calculation-base/controls/group.control";
import { InOutBoxes } from "@bitwarden/web-vault/app/components/charts/shared/in-out-boxes";
import {
  InteractionState,
  InteractionStateKey,
} from "@bitwarden/web-vault/app/components/charts/shared/interaction-state";

@Directive()
export abstract class BaseBalanceChartComponent implements OnInit, OnDestroy {
  protected el: ElementRef<any> = inject(ElementRef);
  protected deviceDetectorService = inject(DeviceDetectorService);
  // elements that need to be initialized by each chart
  protected groupControl: GroupingSelection;
  protected graphContainerSelectString: string;
  protected graphContainerID: string;
  protected graphGroupID: string;

  // graph elements
  protected svg: any;
  protected graphContainer: any;
  protected balanceGraph: any;
  protected defs: any;
  protected gradientIn: any;
  protected gradientOut: any;

  protected margins = {
    top: 30,
    bottom: 30,
    left: 50,
    right: 20,
  } as const;

  protected outerWidth: number = 0;
  protected outerHeight: number = 0;
  protected innerWidth: number;
  protected leftMargin: number;
  protected originalWidthReference: number;
  protected originalHeightReference: number;

  protected markerLine: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;
  protected xScale: d3.ScaleTime<number, number, never>;
  protected yScale: d3.ScaleLinear<number, number, never>;

  // Graph Elements controlled by graph element classes
  protected graphElementsInitialized = false;
  protected scales: Scales;
  protected axis: Axis;
  protected dataLine: DataLine;
  protected dataPoints: DataPoints;
  protected inOutBoxes: InOutBoxes;

  // Graph Data
  protected balancePointData: number[][] = [];
  protected balancePointDates: number[] = []; // Store dates (in seconds) for each point

  /**
   * Data to be passed to the balance HUD component
   */
  hudIndex: number;
  granularity: GranularityProperty;
  pinnedIndex: number;
  protected hoveredIndex: number;

  @HostListener("window:resize")
  onResize() {
    this.resizeGraph();
  }

  protected resizeGraph() {
    const width = this.el.nativeElement.getBoundingClientRect().width;
    const height = 0.5 * width;
    let marginLeft = this.margins.left;
    let marginRight = this.margins.right;
    const marginTop = this.margins.top;
    const marginBottom = this.margins.bottom;

    if (!this.originalWidthReference) {
      this.originalWidthReference = width - marginLeft - marginRight;
      this.outerWidth = width;
    } else {
      const ratio = width / this.outerWidth;
      marginLeft = this.margins.left * ratio;
      marginRight = this.margins.right * ratio;
    }
    if (!this.originalHeightReference) {
      this.originalHeightReference = height - marginTop - marginBottom;
      this.outerHeight = height;
    } else {
      const ratio = height / this.originalHeightReference;
    }

    this.leftMargin = marginLeft;
    this.innerWidth = width - marginLeft - marginRight;
  }

  ngOnInit() {
    this.resizeGraph();
    this.initGraphContainers();
    this.updateGraph();
  }

  /**
   * Implement this function in the child class to update the graph
   */
  updateGraph() {
    this.clearData();
  }

  /**
   * Implement this function in the child class to redraw the in out boxes on mouse move
   */
  updateInOutBoxes(index?: number, type: "pinned" | "hover" = "pinned") {}

  updateGranularity() {
    this.granularity = this.groupControl.granularity;
  }

  initGraphContainers() {
    this.initSVG();
    this.initGroupings();
    this.initDefs();
    this.addMouseMoveEvent();
    this.addMouseDownEvent();
    this.addMouseLeaveEvent();
  }

  // <editor-fold desc="Initialization Functions">
  // TODO: @mmeshel - eventually Alex wants to move to this being recalled on resize so that we don't
  // zoom in and out on the original graph when resizing. He would prefer we re-render so that the scales
  // are set correctly and the font doesn't get larger and smaller
  private initSVG() {
    this.graphContainer = d3.select(this.graphContainerSelectString);
    this.svg = this.graphContainer
      .append("svg")
      .attr("id", this.graphContainerID)
      .attr("viewBox", `0 0 ${this.outerWidth} ${this.outerHeight}`)
      .attr("preserveAspectRatio", "xMidYMax meet");
  }

  private initGroupings() {
    // parent grouping for graph elements
    this.balanceGraph = this.svg
      .append("g")
      .attr("id", this.graphGroupID)
      .attr("transform", `translate(${this.margins.left},${this.margins.top})`);
  }

  private initDefs() {
    this.defs = this.balanceGraph.append("defs");

    this.gradientIn = this.defs
      .append("linearGradient")
      .attr("id", "gradientInBoxes")
      .attr("x1", "0%")
      .attr("x2", "100%")
      .attr("y1", "0%")
      .attr("y2", "0%");

    this.gradientIn
      .append("stop")
      .attr("class", "start")
      .attr("offset", "0%")
      .attr("stop-color", "rgba(90, 143, 0, 0)");

    this.gradientIn
      .append("stop")
      .attr("class", "start")
      .attr("offset", "100%")
      .attr("stop-color", "rgba(90, 143, 0, 0.1)");

    // todo: @Horace for right border, will remove in the border pr
    // // 1. Define the mask
    //     this.defs.append("mask")
    //       .attr("id", "gradientInMask")
    //       .append("rect")
    //       .attr("x", 0)
    //       .attr("y", 0)
    //       .attr("width", this.innerWidth)
    //       .attr("height", this.originalHeightReference)
    //       .attr("fill", "white");
    //
    // // 2. Draw the gradient rect with the mask
    //     this.balanceGraph.append("rect")
    //       .attr("x", 0)
    //       .attr("y", 0)
    //       .attr("width", this.innerWidth)
    //       .attr("height", this.originalHeightReference)
    //       .attr("fill", "url(#gradientInBoxes)")
    //       .attr("mask", "url(#gradientInMask)");
    //
    // // 3. Draw the right border (also masked, if needed)
    //     this.balanceGraph.append("rect")
    //       .attr("x", this.innerWidth - 1)
    //       .attr("y", 0)
    //       .attr("width", 1)
    //       .attr("height", this.originalHeightReference)
    //       .attr("fill", "#5A8F00CC")
    //       .attr("mask", "url(#gradientInMask)");

    this.gradientOut = this.defs
      .append("linearGradient")
      .attr("id", "gradientOutBoxes")
      .attr("x1", "0%")
      .attr("x2", "100%")
      .attr("y1", "0%")
      .attr("y2", "0%");

    this.gradientOut
      .append("stop")
      .attr("class", "start")
      .attr("offset", "0%")
      .attr("stop-color", "rgba(219, 25, 25, 0)");

    this.gradientOut
      .append("stop")
      .attr("class", "start")
      .attr("offset", "100%")
      .attr("stop-color", "rgba(219, 25, 25, 0.1)");
  }

  protected initGraphElements() {
    this.initScales();
    this.initAxis();
    this.initDataPoints();
    this.initDataLine();
    this.initInOutBoxes();
    this.initMarker();
  }

  protected initMarker(xPosition?: number, yPosition?: number) {
    if (!this.markerLine && this.dataLine) {
      // only create the marker if it hasn't been created
      this.markerLine = this.dataLine.createMarkerLine(0, xPosition, yPosition);
    }
  }

  private initScales() {
    this.scales = new Scales();
    this.xScale = this.scales.xScale;
    this.yScale = this.scales.yScale;
  }

  private initAxis() {
    this.axis = new Axis(
      this.balanceGraph,
      this.xScale,
      this.yScale,
      this.deviceDetectorService.isMobile(),
    );
  }

  private initDataPoints() {
    this.dataPoints = new DataPoints(this.balanceGraph, this.xScale, this.yScale);
  }

  private initInOutBoxes() {
    this.inOutBoxes = new InOutBoxes(this.balanceGraph, this.xScale, this.yScale);
  }

  private initDataLine() {
    this.dataLine = new DataLine(this.balanceGraph, this.xScale, this.yScale);
  }

  // </editor-fold desc="Initialization Functions">

  // <editor-fold desc="Graph Drawing Functions">
  protected drawScales(
    startDate: Date,
    endDate: Date,
    minYAxisValue: number,
    maxYAxisValue: number,
    tickValues?: Date[],
  ) {
    if (!this.graphElementsInitialized) {
      this.scales.setScales(
        this.originalWidthReference,
        this.originalHeightReference,
        minYAxisValue,
        maxYAxisValue,
        startDate,
        endDate,
      );

      this.axis.drawAxis(
        this.originalHeightReference,
        startDate,
        endDate,
        this.granularity,
        minYAxisValue,
        maxYAxisValue,
        tickValues,
        "",
        "",
      );
      this.graphElementsInitialized = true;
    } else {
      this.scales.setScales(
        this.originalWidthReference,
        this.originalHeightReference,
        minYAxisValue,
        maxYAxisValue,
        startDate,
        endDate,
      );
      this.axis.redrawAxis(
        this.originalHeightReference,
        startDate,
        endDate,
        this.granularity,
        minYAxisValue,
        maxYAxisValue,
        tickValues,
      );
    }
  }

  // </editor-fold desc="Graph Drawing Functions">

  // <editor-fold desc="Mouse Move Events">
  private addMouseMoveEvent() {
    this.graphContainer.on("mousemove", (e: any) => this.handleMouseMoveEvent(e));
  }

  private addMouseDownEvent() {
    this.graphContainer.on("mousedown", (e: any) => this.handleMouseDownEvent(e));
  }

  protected updateVisuals(index: number, type: InteractionStateKey = InteractionState.Default) {
    if (type === InteractionState.Hover) {
      const [x, y] = this.getPathCoordinates(index);
      this.dataLine.setMarkerLinePinned(false);
      this.dataLine.moveMarkerLine(x, y);
      this.updateInOutBoxes(index, InteractionState.Hover);
      this.setHudData(index);
      this.selectPoint(index);
      this.highlightXAxisForPoint(index);
    } else if (type === InteractionState.Pinned) {
      this.setMarkerPinned();
      this.setHudData(index);
      this.selectPoint(index);
      this.updateInOutBoxes(index, InteractionState.Pinned);
      this.highlightXAxisForPoint(index);
    } else if (type === InteractionState.Default) {
      const lastIndex = this.balancePointData.length - 1;
      const [x, y] = this.getPathCoordinates(lastIndex);
      this.setHudData(lastIndex);
      this.dataLine.setMarkerLinePinned(false, true);
      this.dataLine.moveMarkerLine(x, y);
      this.selectPoint(lastIndex);
      this.updateInOutBoxes();
      this.axis.resetXAxisHighlight();
    }
  }

  protected handleMouseMoveEvent(e: any): any {
    // balancePointData is cleared (see BalanceChartComponent.updateGraph's null path) whenever
    // the store's balancePoints goes null mid-session (e.g. purge vault, log out/in) - without
    // this guard a pointer already over the chart redraws boxes/markers from stale indices.
    if (!this.balancePointData.length) {
      return;
    }
    const closestPointIndex = this.getClosestPointIndex(e);
    this.hoveredIndex = closestPointIndex;
    this.updateVisuals(this.hoveredIndex, InteractionState.Hover);
    if (this.pinnedIndex !== null) {
      this.updateInOutBoxes(this.pinnedIndex, InteractionState.Pinned);
    }
  }

  protected handleMouseDownEvent(e: any): any {
    if (!this.balancePointData.length) {
      return;
    }
    const closestIndex = this.getClosestPointIndex(e);
    this.pinnedIndex = this.pinnedIndex === closestIndex ? null : closestIndex;
    this.hoveredIndex = null;
    this.clearHoverBoxes();
    if (this.pinnedIndex !== null) {
      this.updateVisuals(this.pinnedIndex, InteractionState.Pinned);
    } else {
      this.updateVisuals(null, InteractionState.Default);
    }
  }

  protected addMouseLeaveEvent() {
    this.graphContainer.on("mouseleave", () => {
      if (!this.balancePointData.length) {
        return;
      }
      this.hoveredIndex = null;
      this.clearHoverBoxes();
      if (this.pinnedIndex !== null) {
        this.updateVisuals(this.pinnedIndex, InteractionState.Pinned);
      } else {
        this.updateVisuals(null, InteractionState.Default);
      }
    });
  }

  /**
   * Removes the transient hover in/out boxes without redrawing them. `InOutBoxes.updateInBoxes`/
   * `updateOutBoxes` treat a null index as "draw every box", so calling `updateInOutBoxes(null, Hover)`
   * here (as this used to) left a full stale copy of the boxes under the `-hover` ids that never got
   * cleaned up by the next data refresh — causing the old and new in/out chart to visibly overlap
   * until the next mousemove redrew the hover ids. Removing them directly avoids repopulating that copy.
   */
  protected clearHoverBoxes() {
    this.inOutBoxes?.removeBoxes("inBoxes-hover");
    this.inOutBoxes?.removeBoxes("outBoxes-hover");
  }

  /**
   * Removes the pinned/default in/out boxes without redrawing them (#4603) - symmetric to
   * clearHoverBoxes() above, for the null-data-path cleanup added alongside it. Centralising
   * the "inBoxes"/"outBoxes" id pair here means the 4 call sites (balance-chart and
   * scenario-chart's null paths) can't drift from clearHoverBoxes()'s own id vocabulary.
   */
  protected clearPinnedBoxes() {
    this.inOutBoxes?.removeBoxes("inBoxes");
    this.inOutBoxes?.removeBoxes("outBoxes");
  }

  private getClosestPointIndex(e: any): number {
    const pointerCoords = d3.pointer(e);
    const castToReferenceSpace = (x: number) => {
      return x * (this.innerWidth / this.originalWidthReference);
    };

    const mouseX = pointerCoords[0] - this.leftMargin; // X coordinate of the mouse pointer
    // Find the closest data point based on X coordinate
    let closestPointIndex = this.balancePointData.length - 1;
    let minDistance = Infinity;

    // Loop through all data points to find the closest one
    this.balancePointData.forEach((point, index) => {
      const balancePointX = castToReferenceSpace(point[0]);
      const distance = Math.abs(balancePointX - mouseX);

      if (distance < minDistance) {
        minDistance = distance;
        closestPointIndex = index;
      }
    });

    return closestPointIndex;
  }

  protected getPathCoordinates(index: number): number[] {
    return this.balancePointData?.[index];
  }

  protected setHudData(i: number) {
    this.hudIndex = i;
  }

  protected setMarkerPinned() {
    const [closestPointX, closestPointY] = this.getPathCoordinates(this.pinnedIndex);
    this.dataLine.moveMarkerLine(closestPointX, closestPointY);
    this.dataLine.setMarkerLinePinned(true, false);
  }

  protected drawInsAndOuts(
    balancesByTime: Map<string, number>,
    insByTime: Map<string, number>,
    outsByTime: Map<string, number>,
    index?: number,
    inClass: string = "inBoxes",
    outClass: string = "outBoxes",
  ) {
    this.inOutBoxes.updateInBoxes(balancesByTime, insByTime, inClass, index);
    this.inOutBoxes.updateOutBoxes(balancesByTime, outsByTime, outClass, index);
  }

  // Overwrite this method in the child class to handle point selection
  selectPoint(i: number) {}

  protected highlightXAxisForPoint(index: number) {
    this.axis.highlightXAxisTick(this.balancePointDates[index]);
  }

  clearData() {
    this.hudIndex = null;
    this.pinnedIndex = null;
    this.hoveredIndex = null;
    this.clearHoverBoxes();
  }

  ngOnDestroy() {
    this.originalHeightReference = null;
    this.originalWidthReference = null;
    this.hudIndex = null;
  }

  // </editor-fold desc="Mouse Move Events">
}
